import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import '../../shared/ReactSelectDarkMode.css';
import { IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { api } from '../../shared/api';
import { CenteredLoader } from '../../components/LoadingSpinner';
import PageSection from '../../components/layout/PageSection';
import PageShell from '../../components/layout/PageShell';
import { useConfirm } from '../../shared/ConfirmContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRealtimeEvents } from '../../shared/useRealtimeEvents';
import CaseFormModal from './components/CaseFormModal';
import CaseListTable from './components/CaseListTable';
import { EMPTY_CASE_FORM, buildCasePayload, changeCaseUnit, editCaseForm, newCaseForm } from './caseForm';

const CaseList = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(EMPTY_CASE_FORM);

  const [stockItemOptions, setStockItemOptions] = useState([]);
  const [selectedStockItem, setSelectedStockItem] = useState(null);

  const { data: cases = [], isLoading: loading } = useQuery({
    queryKey: ['item-cases'],
    queryFn: async () => {
      const response = await api.get('warehouses/api/item-cases/');
      return response.results || response || [];
    },
    onError: () => setError(t('warehouses.cases.loadError')),
  });

  const { data: weightUnits = [] } = useQuery({
    queryKey: ['weight-units'],
    queryFn: () => api.get('settings/api/weight-units/?page_size=100'),
  });

  const { data: dimensionUnits = [] } = useQuery({
    queryKey: ['dimension-units'],
    queryFn: () => api.get('settings/api/dimension-units/?page_size=100'),
  });

  useRealtimeEvents((e) => {
    if (e.model === 'warehouses.itemcase' || e.model === 'warehouses.caseunit') {
      queryClient.invalidateQueries({ queryKey: ['item-cases'] });
    }
  });

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = buildCasePayload(formData, selectedStockItem);
      return editing
        ? api.patch(`warehouses/api/item-cases/${editing.id}/`, payload)
        : api.post('warehouses/api/item-cases/', payload);
    },
    onSuccess: () => {
      setShowModal(false);
      setSuccess(editing ? t('warehouses.cases.editCase') : t('warehouses.cases.newCase'));
      setTimeout(() => setSuccess(null), 3000);
      queryClient.invalidateQueries({ queryKey: ['item-cases'] });
    },
    onError: () => {
      setError(t('warehouses.bundles.addError'));
      setTimeout(() => setError(null), 5000);
    },
  });
  const saving = saveMutation.isPending;

  const deleteMutation = useMutation({
    mutationFn: (c) => api.delete(`warehouses/api/item-cases/${c.id}/`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['item-cases'] }),
    onError: () => {
      setError(t('warehouses.bundles.addError'));
      setTimeout(() => setError(null), 5000);
    },
  });

  const handleStockItemSearch = useCallback((inputValue) => {
    if (!inputValue || inputValue.length < 2) { setStockItemOptions([]); return; }
    const search = async () => {
      try {
        const response = await api.get(
          `warehouses/api/stock-items/?search=${encodeURIComponent(inputValue)}&page_size=50`
        );
        const items = Array.isArray(response) ? response : (response.results || []);
        setStockItemOptions(items.map(item => ({
          value: item.id,
          label: `${item.product_name} (${item.stock_name || item.stock})`,
          itemName: item.product_name,
        })));
      } catch (err) { console.error(err); }
    };
    search();
  }, []);

  const handleStockItemChange = (option) => {
    setSelectedStockItem(option);
    if (option) {
      setFormData(f => ({ ...f, name: `${option.itemName || option.label.replace(/ \(.*\)$/, '')} - Case` }));
    }
  };

  const openAdd = () => {
    setEditing(null);
    setFormData(newCaseForm(weightUnits, dimensionUnits));
    setSelectedStockItem(null);
    setStockItemOptions([]);
    setShowModal(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setFormData(editCaseForm(c, weightUnits, dimensionUnits));
    setSelectedStockItem(c.stock_item ? { value: c.stock_item, label: c.stock_item_name || String(c.stock_item), itemName: c.stock_item_name || String(c.stock_item) } : null);
    setStockItemOptions([]);
    setShowModal(true);
  };

  const handleSave = () => {
    if (!formData.name.trim()) return;
    saveMutation.mutate();
  };

  const handleDelete = async (c) => {
    if (!await confirm(t('warehouses.cases.confirmDelete', { name: c.name }))) return;
    deleteMutation.mutate(c);
  };

  const handleFormFieldChange = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  // Switching a unit only changes what the entered number means; the number itself stays as typed.
  const handleUnitChange = (field, option) => {
    setFormData((current) => changeCaseUnit(current, field, option));
  };

  if (loading) {
    return (
      <PageShell
        pretitle={t('warehouses.overview')}
        title={t('warehouses.cases.title')}
        description={t('warehouses.cases.subtitle', 'Manage case definitions, stock links and available quantities from the shared warehouse list layout.')}
      >
        <PageSection>
          <CenteredLoader />
        </PageSection>
      </PageShell>
    );
  }

  return (
    <PageShell
      pretitle={t('warehouses.overview')}
      title={t('warehouses.cases.title')}
      description={t('warehouses.cases.subtitle', 'Manage case definitions, stock links and available quantities from the shared warehouse list layout.')}
      actions={
        <Button variant="primary" onClick={openAdd}>
          <IconPlus size={16} className="me-1" />
          {t('warehouses.cases.newCase')}
        </Button>
      }
    >
      {error && <div className="alert alert-danger app-inline-alert" role="alert">{error}</div>}
      {success && <div className="alert alert-success app-inline-alert" role="alert">{success}</div>}

      <PageSection
        title={<>{t('warehouses.cases.title')}<span className="badge bg-secondary ms-2">{cases.length}</span></>}
        description={t('warehouses.cases.listDescription', 'Open an existing case, edit metadata, or create a new stock-linked case from one surface.')}
        bodyClassName="p-0"
      >
        <CaseListTable
          cases={cases}
          onCreate={openAdd}
          onEdit={openEdit}
          onDelete={handleDelete}
        />
      </PageSection>

      <CaseFormModal
        show={showModal}
        editing={Boolean(editing)}
        formData={formData}
        selectedStockItem={selectedStockItem}
        stockItemOptions={stockItemOptions}
        saving={saving}
        onClose={() => setShowModal(false)}
        onSave={handleSave}
        onFieldChange={handleFormFieldChange}
        onUnitChange={handleUnitChange}
        onStockItemChange={handleStockItemChange}
        onStockItemSearch={handleStockItemSearch}
      />
    </PageShell>
  );
};

export default CaseList;
